import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X } from "lucide-react";
import { useTranslation } from "@/i18n";
import { parseJoinCode } from "../../utils/quickLoginQr";

/**
 * QrLoginScanner - point this phone at the owner's screen to join their account.
 *
 * For the case the native-camera flow misses: someone who already has the app
 * open on the login screen. Rather than making them read six digits off
 * another phone, they scan the QR directly.
 *
 * Uses the same html5-qrcode scanner the barcode features already rely on, so
 * there is no second camera stack to maintain or permission to explain twice.
 *
 * Anything scanned that isn't a valid join code is ignored and scanning
 * continues - pointing at a cereal box should do nothing at all, not throw an
 * error at someone who is simply aiming badly.
 */
export default function QrLoginScanner({ onCode, onClose }) {
  const { t } = useTranslation();
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const stop = () => {
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) s.stop().catch(() => {});
    };

    const timer = setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("qr-login-reader");
        if (cancelled) return;
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
          (decodedText) => {
            const code = parseJoinCode(decodedText);
            // Ignore non-join codes and keep looking; only fire once.
            if (!code || handledRef.current) return;
            handledRef.current = true;
            stop();
            onCode(code);
          },
          () => { /* no QR in frame - normal, ignore */ }
        );
      } catch (e) {
        if (!cancelled) setError(t("join.cameraDenied"));
      }
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="qr-login-scanner">
      <div className="flex justify-between items-center p-4 text-white">
        <span className="font-semibold">{t("join.scanTitle")}</span>
        <button onClick={onClose} aria-label={t("common.close")} data-testid="qr-scanner-close">
          <X size={26} />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div id="qr-login-reader" className="w-full max-w-xs rounded-2xl overflow-hidden" />
        {error ? (
          <p className="text-red-400 text-sm mt-4 text-center">{error}</p>
        ) : (
          <p className="text-white/70 text-sm mt-4 text-center">{t("join.scanHint")}</p>
        )}
      </div>
    </div>
  );
}
